import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, shift } from "@floating-ui/react";
import usePopoverClose from "../../../../hooks/usePopoverClose";
import { useProjectStore } from "../../../../stores/projectStore";
import { StyledContainer, StyledOverlay } from "./styles";
import TextButton from "../../../TextButton";
import { useToast } from "../../../ToastWrapper";

export const EmptyTrashConfirm = ({ setShowEmptyTrashConfirm, setShowDeleted, totalItems, anchorRef }) => {
  const project = useProjectStore((state) => state.project);
  const deletedDocuments = useProjectStore((state) => state.deletedDocuments);
  const setDocumentId = useProjectStore((state) => state.setDocumentId);
  const { setToast } = useToast();

  const [countdown, setCountdown] = useState(3);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showFinalCancel, setShowFinalCancel] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  const cancelRequestRef = useRef(false);
  const firstButtonRef = useRef(null);
  const { refs, floatingStyles, update } = useFloating({
    placement: "top-start",
    middleware: [offset(12), shift({ padding: 16 })],
  });

  const onClose = () => {
    setShowEmptyTrashConfirm(false);
    anchorRef?.current?.focus();
  };

  const { containerRef, handleBlur } = usePopoverClose(true, onClose);

  // no longer show the empty trash confirm if the user cancels the countdown
  useEffect(() => {
    if (cancelRequestRef.current) {
      setShowEmptyTrashConfirm(false);
    }
  }, [cancelRequestRef.current]);

  useEffect(() => {
    if (anchorRef?.current) {
      refs.setReference(anchorRef.current);
      update();
    }
  }, [anchorRef, refs, update]);

  const setPopoverNode = (node) => {
    containerRef.current = node;
    refs.setFloating(node);
    if (node) {
      requestAnimationFrame(() => firstButtonRef.current?.focus());
    }
  };

  const countdownTimer = () => {
    return new Promise((resolve, reject) => {
      let next = countdown - 1;
      const timer = setInterval(() => {
        setCountdown(next);
        next--;
        if (next < 0) {
          // countdown is showing 0
          setShowFinalCancel(false);
          clearInterval(timer);
          resolve(true);
        }
      }, 1000);
    });
  };

  const confirmDelete = async () => {
    setShowCountdown(true);
    const finalConfirm = await countdownTimer();

    if (finalConfirm && !cancelRequestRef.current) {
      setIsDeleting(true);
      try {
        const totalItems = deletedDocuments.length;
        const numDeleted = await project.emptyTrash();

        // go back to the first document in the project, and hide the deleted documents
        setShowDeleted(false);
        const nextDocumentId = project.contents?.[0]?.id;
        if (nextDocumentId) {
          setDocumentId(nextDocumentId);
        }

        if (numDeleted < totalItems) {
          setToast(`Deleted ${numDeleted} of ${totalItems} items. Some items could not be removed.`);
        } else {
          setToast(`All items were permanently deleted`);
        }
      } catch (e) {
        setToast("Failed to empty trash. Please try again.");
      }
      // Close the dialog last — component unmounts, so no state updates after this
      setShowEmptyTrashConfirm(false);
      return;
    }

    setShowCountdown(false);
    setCountdown(3);
    setIsDeleting(false);
  };

  const popover = (
    <StyledOverlay>
      <StyledContainer
        ref={setPopoverNode}
        className="empty-trash-confirm"
        style={floatingStyles}
        role="dialog"
        aria-label="Confirm empty trash"
        onBlur={handleBlur}
      >
      {!showCountdown && (
        <>
          <p className="heading">Are you Sure?</p>
          <div className="text-content">
            <p>This will permanently delete all trashed items.</p>
            {project.data.seriesId ? <p>Trashed Characters and Worldbuilding will be removed from all projects in this series.</p> : null}
          </div>
          <div className="choices">
            <TextButton
              ref={firstButtonRef}
              className="cancel link-style"
              aria-label="Cancel empty trash operation"
              onClick={() => setShowEmptyTrashConfirm(false)}
            >
              Nevermind
            </TextButton>
            <TextButton
              className="confirm link-style"
              aria-label="Confirm empty trash operation"
              onClick={() => confirmDelete()}
            >
              Yes, I'm sure
            </TextButton>
          </div>
        </>
      )}

      {showCountdown && (
        <>
          {isDeleting ? (
            <>
              <p className="heading countdown-heading">Emptying trash...</p>
            </>
          ) : (
            <>
              <p className="heading countdown-heading">Deleting in...</p>
              <p className="countdown-num">{countdown}</p>
              <div className="choices final">
                {showFinalCancel && (
                  <button
                    className="cancel final-cancel link-style"
                    aria-label="Cancel empty trash operation"
                    onClick={() => (cancelRequestRef.current = true)}
                  >
                    Wait! No! Stop!
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
      </StyledContainer>
    </StyledOverlay>
  );

  return createPortal(popover, document.body);
};
